import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Sparkle, XCircle } from '@phosphor-icons/react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CompareToggle from '../components/CompareToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_ORIGIN = 'https://servermemoryrfq.com';

const setMeta = (name, content) => {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
};

const ItemCard = ({ item }) => {
  const qty = parseInt(item.qty_on_hand) || 0;
  return (
    <div className="bg-[#11141D] border border-[#1E293B] hover:border-cyan-500/60 rounded-lg p-4 transition-colors" data-testid={`category-item-${item.slug}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${qty > 0 ? 'text-[#10B981] bg-[#10B981]/10' : 'text-[#F59E0B] bg-[#F59E0B]/10'}`}>
          {qty > 0 ? `In Stock · ${qty}` : 'Pre-order'}
        </span>
        <CompareToggle item={item} testId={`category-compare-${item.slug}`} />
      </div>
      <Link to={`/product/${item.slug}`} className="block font-mono font-bold text-cyan-400 hover:text-white break-all transition-colors">{item.name}</Link>
      <p className="text-xs text-[#94A3B8] mt-1">{item.description}</p>
      <div className="flex gap-3 mt-3 text-[11px] font-mono text-[#64748B]">
        {item.parsed?.speed && <span>{item.parsed.speed} MT/s</span>}
        {item.parsed?.rank && <span>{item.parsed.rank}</span>}
        {item.parsed?.volt && <span>{item.parsed.volt}</span>}
        {item.taa === 'Yes' && <span className="text-[#007AFF]">TAA</span>}
      </div>
    </div>
  );
};

export default function CategoryPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false); window.scrollTo(0, 0);
    axios.get(`${API_URL}/api/categories/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    const { category } = data;
    document.title = `${category.title} — ${category.count} Part Numbers | ServerMemoryRFQ`;
    setMeta('description', (category.intro || `Shop ${category.count} ${category.title} modules in stock at ServerMemoryRFQ.`).slice(0, 300));
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.rel = 'canonical'; document.head.appendChild(link); }
    link.href = `${SITE_ORIGIN}/category/${category.slug}`;
  }, [data]);

  if (notFound) {
    return (
      <div className="min-h-screen"><Header />
        <div className="max-w-5xl mx-auto px-4 py-24 text-center" data-testid="category-not-found">
          <XCircle size={56} className="mx-auto text-[#94A3B8] mb-4" />
          <h1 className="text-3xl font-['JetBrains_Mono'] font-bold mb-4">Category not found</h1>
          <Link to="/sitemap" className="text-cyan-400 font-mono">Browse the site map →</Link>
        </div>
        <Footer />
      </div>
    );
  }
  if (!data) return <div className="min-h-screen"><Header /><div className="py-24 text-center font-mono text-[#94A3B8]">Loading…</div></div>;

  const { category, items, related_categories: related } = data;
  return (
    <div className="min-h-screen" data-testid="category-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-10">
        <nav className="mb-6 text-sm font-mono text-[#94A3B8]" data-testid="category-breadcrumb">
          <Link to="/" className="hover:text-cyan-400">Home</Link><span className="mx-2">/</span>
          <Link to="/sitemap" className="hover:text-cyan-400">Site Map</Link><span className="mx-2">/</span>
          <span className="text-white">{category.title}</span>
        </nav>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-cyan-500 mb-3">{category.type} · {category.form_factor} · {category.capacity_gb}GB</p>
        <h1 className="text-4xl sm:text-5xl font-['JetBrains_Mono'] font-bold text-white mb-5" data-testid="category-title">{category.title}</h1>
        {category.intro && (
          <p className="text-[#CBD5E1] leading-relaxed max-w-3xl mb-3" data-testid="category-intro">
            <Sparkle size={14} weight="fill" className="inline mr-2 text-[#A855F7]" />{category.intro}
          </p>
        )}
        <p className="text-xs font-mono text-[#64748B] mb-10" data-testid="category-count">{items.length} part numbers in live inventory</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
          {items.map((i) => <ItemCard key={i.slug} item={i} />)}
        </div>

        {related.length > 0 && (
          <section data-testid="related-categories">
            <h2 className="text-lg font-['JetBrains_Mono'] font-bold text-white mb-4">More {category.type} categories</h2>
            <div className="flex flex-wrap gap-2">
              {related.map((c) => (
                <Link key={c.slug} to={`/category/${c.slug}`} className="px-3 py-1.5 rounded border border-[#334155] hover:border-cyan-400 text-xs font-mono text-[#CBD5E1] transition-colors" data-testid={`related-category-${c.slug}`}>
                  {c.title.replace(' Server Memory', '')} <span className="text-[#64748B]">({c.count})</span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
